// crm-web/frontend/src/components/analytics/RefreshControl.jsx
// Boton de refresh manual + indicador "hace X min".
// Si hay nueva version del servidor disponible, se transforma en un CTA
// prominente "Nueva version - Recargar" para invitar al usuario.

import { useEffect, useState } from 'react'

function formatRelative(ms) {
  const secs = Math.floor(ms / 1000)
  if (secs < 10) return 'ahora'
  if (secs < 60) return `hace ${secs}s`
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `hace ${mins} min`
  const hrs = Math.floor(mins / 60)
  return `hace ${hrs}h`
}

export default function RefreshControl({
  lastRefresh,
  onRefresh,
  isRefreshing = false,
  hasUpdate = false,
  onReload,
}) {
  const [, setTick] = useState(0)

  // Forzar re-render cada 10s para actualizar el texto "hace X min"
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10000)
    return () => clearInterval(id)
  }, [])

  // Modo "nueva version disponible" - prominente
  if (hasUpdate) {
    return (
      <button
        onClick={onReload}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600
                   hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-md
                   transition animate-pulse-slow"
        style={{ animation: 'pulse 2.5s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
        title="Hay una nueva versión del CRM. Click para recargar la página."
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span className="text-sm">Nueva versión — Recargar</span>
      </button>
    )
  }

  // Modo normal
  const relativeStr = lastRefresh ? formatRelative(Date.now() - lastRefresh) : '—'
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
        Actualizado {relativeStr}
      </span>
      <button
        onClick={onRefresh}
        disabled={isRefreshing}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition font-medium ${
          isRefreshing
            ? 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed'
            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary-400 dark:hover:border-primary-500'
        }`}
        title="Actualizar ahora"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        <span className="text-sm">Actualizar</span>
      </button>
    </div>
  )
}
